import React, { useState, useMemo } from "react";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import {
  getMapCalibration,
  computeCalibration,
  parseGetPos,
} from "../../constants/mapCalibration";

const CAL_RENDER_SIZE = 700;
const DEFAULT_IMAGE_SIZE = 1024;

// Recognizable, well-spread landmarks make for the most accurate fit. Pick
// spots near opposite extremes of the map so the solver has a wide baseline.
const SUGGESTED_LANDMARKS = {
  de_mirage: [
    "T Spawn (back wall)",
    "CT Spawn",
    "A site — default plant (triple box)",
    "B site — inside Market",
    "Mid — under Top Mid / window",
    "Palace balcony",
    "Apartments (Apps) entrance",
    "B Short / Cat corner",
  ],
};

function nextLabel(mapId, index) {
  const list = SUGGESTED_LANDMARKS[mapId];
  if (list && list[index]) return list[index];
  return `Point ${index + 1}`;
}

export default function CalibrationPanel({ defaultMapId }) {
  const initialMap = defaultMapId || "de_mirage";
  const initialCal = getMapCalibration(initialMap);

  const [mapId, setMapId] = useState(initialMap);
  const [imageUrl, setImageUrl] = useState(initialCal ? initialCal.imageUrl : "");
  const [imageSize, setImageSize] = useState(
    initialCal ? initialCal.imageSize : DEFAULT_IMAGE_SIZE
  );
  const [points, setPoints] = useState([]);
  const [copied, setCopied] = useState(false);

  const ratio = CAL_RENDER_SIZE / (imageSize || DEFAULT_IMAGE_SIZE);

  const result = useMemo(() => computeCalibration(points), [points]);

  const handleImageClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    setPoints((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        srcPx: clickX / ratio,
        srcPy: clickY / ratio,
        worldX: NaN,
        worldY: NaN,
        label: nextLabel(mapId, prev.length),
        raw: "",
      },
    ]);
    setCopied(false);
  };

  const updatePoint = (id, patch) => {
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    setCopied(false);
  };

  const onPaste = (id, raw) => {
    const parsed = parseGetPos(raw);
    updatePoint(id, {
      raw,
      worldX: parsed ? parsed.x : NaN,
      worldY: parsed ? parsed.y : NaN,
    });
  };

  const removePoint = (id) => {
    setPoints((prev) => prev.filter((p) => p.id !== id));
    setCopied(false);
  };

  const snippet =
    result && !result.error
      ? `${mapId}: {\n` +
        `  imageUrl: "${imageUrl}",\n` +
        `  worldTopLeftX: ${result.worldTopLeftX},\n` +
        `  worldTopLeftY: ${result.worldTopLeftY},\n` +
        `  scale: ${result.scale},\n` +
        `  imageSize: ${imageSize},\n` +
        `},`
      : "";

  const copySnippet = () => {
    if (!snippet) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(snippet).then(() => setCopied(true));
    }
  };

  return (
    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div>
        <Typography variant="body2" style={{ marginBottom: 6, maxWidth: CAL_RENDER_SIZE }}>
          Click the exact map spot where you're standing, then paste your{" "}
          <code>getpos</code> output into that point's row. Add 3–4 points spread
          across the map, then copy the computed values back to your assistant.
        </Typography>
        <div
          style={{
            position: "relative",
            width: CAL_RENDER_SIZE,
            height: CAL_RENDER_SIZE,
            border: "1px solid #ccc",
            cursor: "crosshair",
          }}
          onClick={handleImageClick}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={mapId}
              width={CAL_RENDER_SIZE}
              height={CAL_RENDER_SIZE}
              style={{ position: "absolute", top: 0, left: 0, userSelect: "none", pointerEvents: "none" }}
            />
          ) : (
            <div style={{ padding: 16 }}>Set an image URL to begin.</div>
          )}
          {points.map((p, i) => (
            <div
              key={p.id}
              style={{
                position: "absolute",
                left: p.srcPx * ratio - 8,
                top: p.srcPy * ratio - 8,
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: Number.isFinite(p.worldX) ? "rgba(40,180,90,0.9)" : "rgba(255,160,0,0.9)",
                color: "#fff",
                fontSize: 11,
                lineHeight: "16px",
                textAlign: "center",
                border: "1px solid #fff",
                pointerEvents: "none",
              }}
            >
              {i + 1}
            </div>
          ))}
        </div>
      </div>

      <div style={{ minWidth: 360, flex: 1 }}>
        <Paper style={{ padding: 12, marginBottom: 12 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            In-game steps
          </Typography>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
            <li>Load the map on a local server: <code>map {mapId}</code></li>
            <li>Enable the console overlay (optional): <code>cl_showpos 1</code></li>
            <li>Stand precisely on a landmark (see suggestions below).</li>
            <li>Open the console (<code>~</code>) and type <code>getpos</code>.</li>
            <li>Copy the whole <code>setpos … ;setang …</code> line it prints.</li>
            <li>Click that same spot on the map, then paste into its row.</li>
          </ol>
          {SUGGESTED_LANDMARKS[mapId] && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Suggested spots: {SUGGESTED_LANDMARKS[mapId].join(" · ")}
            </Typography>
          )}
        </Paper>

        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <TextField
            label="Map id"
            value={mapId}
            onChange={(e) => setMapId(e.target.value.trim())}
            style={{ width: 140 }}
          />
          <TextField
            label="Image size (px, square)"
            type="number"
            value={imageSize}
            onChange={(e) => setImageSize(Number(e.target.value) || DEFAULT_IMAGE_SIZE)}
            style={{ width: 160 }}
          />
          <TextField
            label="Radar image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value.trim())}
            style={{ flex: 1, minWidth: 220 }}
          />
        </div>

        {points.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No points yet — click the map to add one.
          </Typography>
        )}

        {points.map((p, i) => (
          <Paper key={p.id} style={{ padding: 8, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <strong>#{i + 1}</strong>
              <TextField
                value={p.label}
                onChange={(e) => updatePoint(p.id, { label: e.target.value })}
                style={{ flex: 1 }}
              />
              <Button size="small" onClick={() => removePoint(p.id)}>Remove</Button>
            </div>
            <TextField
              label="Paste getpos output (or 'x y')"
              value={p.raw}
              onChange={(e) => onPaste(p.id, e.target.value)}
              fullWidth
            />
            <Typography variant="caption" color={Number.isFinite(p.worldX) ? "textSecondary" : "error"}>
              {Number.isFinite(p.worldX)
                ? `world X ${p.worldX}, Y ${p.worldY} · pixel ${p.srcPx.toFixed(0)}, ${p.srcPy.toFixed(0)}`
                : "waiting for valid world coords…"}
              {result && !result.error && result.residuals[i] != null
                ? ` · fit error ${result.residuals[i].toFixed(1)}u`
                : ""}
            </Typography>
          </Paper>
        ))}

        <Paper style={{ padding: 12, marginTop: 12 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Result</Typography>
          {result && result.error ? (
            <Typography variant="body2" color="textSecondary">{result.error}</Typography>
          ) : (
            <>
              <Typography variant="body2" color={result.maxError > 25 ? "error" : "textSecondary"}>
                worldTopLeftX {result.worldTopLeftX} · worldTopLeftY {result.worldTopLeftY} · scale {result.scale} · max fit error {result.maxError}u
                {result.maxError > 25 ? " (high — recheck a point or use a square official radar image)" : ""}
              </Typography>
              <pre style={{ background: "#f5f5f5", padding: 8, fontSize: 12, overflowX: "auto" }}>{snippet}</pre>
              <Button variant="contained" color="primary" size="small" onClick={copySnippet}>
                {copied ? "Copied" : "Copy values"}
              </Button>
              <Typography variant="caption" style={{ display: "block", marginTop: 6 }}>
                Send these values back to your assistant to bake into <code>mapCalibration.js</code>.
              </Typography>
            </>
          )}
        </Paper>
      </div>
    </div>
  );
}
